import type { UseEmblaCarouselType } from 'embla-carousel-react';

type CarouselApi = NonNullable<UseEmblaCarouselType[1]>;

const VISIBILITY_TOLERANCE_PX = 1;

export function areCarouselPageSnapsEqual(a: number[], b: number[]): boolean {
    return a.length === b.length && a.every((value, index) => value === b[index]);
}

type SlideVisibility = {
    fullyVisible: boolean;
    visible: boolean;
    /** Partially visible on the trailing (right) edge of a horizontal viewport. */
    peekingEnd: boolean;
};

function getSlideVisibility(slide: HTMLElement, viewport: HTMLElement): SlideVisibility {
    const slideRect = slide.getBoundingClientRect();
    const viewRect = viewport.getBoundingClientRect();

    const fullyVisible =
        slideRect.left >= viewRect.left - VISIBILITY_TOLERANCE_PX &&
        slideRect.right <= viewRect.right + VISIBILITY_TOLERANCE_PX;

    const visible =
        slideRect.right > viewRect.left + VISIBILITY_TOLERANCE_PX &&
        slideRect.left < viewRect.right - VISIBILITY_TOLERANCE_PX;

    const peekingEnd =
        visible &&
        !fullyVisible &&
        slideRect.left >= viewRect.left - VISIBILITY_TOLERANCE_PX &&
        slideRect.right > viewRect.right + VISIBILITY_TOLERANCE_PX;

    return { fullyVisible, visible, peekingEnd };
}

function countFullyVisibleSlides(api: CarouselApi): number {
    const slides = api.slideNodes();
    const viewport = api.rootNode();
    if (!slides.length || !viewport) {
        return 1;
    }

    let count = 0;
    for (let i = 0; i < slides.length; i++) {
        if (getSlideVisibility(slides[i], viewport).fullyVisible) {
            count++;
        }
    }

    return Math.max(1, count);
}

/** How many slides fit fully in the viewport (measured from the first snap). */
function measurePageCapacity(api: CarouselApi): number {
    const selected = api.selectedScrollSnap();
    api.scrollTo(0, true);
    const count = countFullyVisibleSlides(api);
    api.scrollTo(selected, true);
    return count;
}

export function getPageCapacityFromSnaps(pageSnaps: number[]): number {
    if (pageSnaps.length >= 2) {
        return Math.max(1, pageSnaps[1] - pageSnaps[0]);
    }
    return 1;
}

/** Leftmost slide intersecting the viewport (anchor for the current "page"). */
export function getLeftmostVisibleSlideIndex(api: CarouselApi): number {
    const slides = api.slideNodes();
    const viewport = api.rootNode();
    if (!slides.length || !viewport) {
        return api.selectedScrollSnap();
    }

    for (let i = 0; i < slides.length; i++) {
        if (getSlideVisibility(slides[i], viewport).visible) {
            return i;
        }
    }

    return api.selectedScrollSnap();
}

/**
 * Index to scroll to for the next "page": the first slide that is not fully visible,
 * typically the tile peeking at the end of the viewport.
 */
export function getNextPageScrollIndex(api: CarouselApi, pageCapacity = 1): number | null {
    const slides = api.slideNodes();
    const viewport = api.rootNode();
    if (!slides.length || !viewport) {
        return null;
    }

    let lastFullyVisibleIndex = -1;

    for (let i = 0; i < slides.length; i++) {
        const { fullyVisible, peekingEnd } = getSlideVisibility(slides[i], viewport);

        if (peekingEnd) {
            return resolveNextPageIndex(slides.length, lastFullyVisibleIndex, i, pageCapacity);
        }

        if (fullyVisible) {
            lastFullyVisibleIndex = i;
        }
    }

    if (lastFullyVisibleIndex >= 0 && lastFullyVisibleIndex < slides.length - 1) {
        return resolveNextPageIndex(slides.length, lastFullyVisibleIndex, lastFullyVisibleIndex + 1, pageCapacity);
    }

    return null;
}

function resolveNextPageIndex(
    slideCount: number,
    lastFullyVisibleIndex: number,
    candidateIndex: number,
    pageCapacity: number
): number | null {
    if (candidateIndex >= slideCount) {
        return null;
    }

    const remainder = slideCount - candidateIndex;
    if (remainder > 0 && remainder < pageCapacity) {
        if (remainder === 1 && lastFullyVisibleIndex === candidateIndex - 1) {
            return lastFullyVisibleIndex;
        }
        return candidateIndex;
    }

    return candidateIndex;
}

function getNextPageScrollIndexAtSnap(api: CarouselApi, snapIndex: number, pageCapacity: number): number | null {
    const selected = api.selectedScrollSnap();
    api.scrollTo(snapIndex, true);
    const nextIndex = getNextPageScrollIndex(api, pageCapacity);
    api.scrollTo(selected, true);
    return nextIndex;
}

/** Snap indices where each "page" starts (first tile aligned to the viewport start). */
export function buildCarouselPageSnapIndices(api: CarouselApi): number[] {
    const slideCount = api.slideNodes().length;
    if (slideCount === 0) {
        return [];
    }

    const pageCapacity = measurePageCapacity(api);
    const snaps: number[] = [0];
    let guard = 0;

    while (guard++ <= slideCount) {
        const current = snaps[snaps.length - 1];
        const next = getNextPageScrollIndexAtSnap(api, current, pageCapacity);
        if (next === null || next <= current) {
            break;
        }
        snaps.push(next);
    }

    return snaps;
}

/**
 * Index to scroll to for the previous "page": the start snap where the current leftmost
 * visible tile was the peeking tile at the end of that prior view.
 */
export function getPrevPageScrollIndex(api: CarouselApi): number | null {
    const leftmostVisible = getLeftmostVisibleSlideIndex(api);
    if (leftmostVisible <= 0) {
        return null;
    }

    const pageCapacity = measurePageCapacity(api);
    for (let snapIndex = leftmostVisible - 1; snapIndex >= 0; snapIndex--) {
        const nextFromSnap = getNextPageScrollIndexAtSnap(api, snapIndex, pageCapacity);
        if (nextFromSnap === leftmostVisible) {
            return snapIndex;
        }
    }

    return 0;
}

export function getCurrentPageIndex(pageSnaps: number[], selectedSnap: number): number {
    if (!pageSnaps.length) {
        return 0;
    }

    const lastPageStart = pageSnaps[pageSnaps.length - 1];
    if (selectedSnap >= lastPageStart) {
        return pageSnaps.length - 1;
    }

    let pageIndex = 0;
    for (let i = 0; i < pageSnaps.length; i++) {
        if (pageSnaps[i] <= selectedSnap) {
            pageIndex = i;
        } else {
            break;
        }
    }

    return pageIndex;
}

function isLastSlideFullyVisible(api: CarouselApi): boolean {
    const slides = api.slideNodes();
    const viewport = api.rootNode();
    if (!slides.length || !viewport) {
        return false;
    }

    return getSlideVisibility(slides[slides.length - 1], viewport).fullyVisible;
}

/**
 * A drag can settle before the last page-start snap while the last cards are already
 * in view, or while Embla's own scroll limit (`trimSnaps` / `keepSnaps`) has been reached.
 * Must not call `scrollTo` (used from Embla `select` / `scroll` handlers).
 */
function isAtLastPageView(api: CarouselApi): boolean {
    if (isLastSlideFullyVisible(api)) {
        return true;
    }

    return typeof api.canScrollNext === 'function' && !api.canScrollNext();
}

/**
 * Current page from Embla's settled snap (not leftmost visible — earlier tiles can
 * still peek on the left after a page forward, which would skew prev navigation).
 * The visual end of the track counts as the last page even when the selected snap
 * is still before that page's start index.
 */
export function getCurrentPageIndexFromSnap(api: CarouselApi, pageSnaps: number[]): number {
    if (!pageSnaps.length) {
        return 0;
    }
    if (isAtLastPageView(api)) {
        return pageSnaps.length - 1;
    }
    return getCurrentPageIndex(pageSnaps, api.selectedScrollSnap());
}

/** @deprecated Use {@link getCurrentPageIndexFromSnap}. */
export function getCurrentPageIndexFromViewport(api: CarouselApi, pageSnaps: number[]): number {
    return getCurrentPageIndexFromSnap(api, pageSnaps);
}

/**
 * Whether more pages exist — must not call `scrollTo` (used from Embla `select` handlers).
 */
export function canScrollToNextPage(api: CarouselApi, pageSnaps: number[]): boolean {
    if (isAtLastPageView(api)) {
        return false;
    }
    if (pageSnaps.length > 0) {
        const pageIndex = getCurrentPageIndexFromSnap(api, pageSnaps);
        return pageIndex < pageSnaps.length - 1;
    }
    return getNextPageScrollIndex(api, measurePageCapacity(api)) !== null;
}

export function canScrollToPrevPage(api: CarouselApi, pageSnaps: number[]): boolean {
    if (!pageSnaps.length) {
        return false;
    }
    return getCurrentPageIndexFromSnap(api, pageSnaps) > 0;
}

export function scrollToNextPage(api: CarouselApi, pageSnaps: number[]): void {
    const pageCapacity = pageSnaps.length > 0 ? getPageCapacityFromSnaps(pageSnaps) : measurePageCapacity(api);
    const nextIndex = getNextPageScrollIndex(api, pageCapacity);
    if (nextIndex !== null) {
        api.scrollTo(nextIndex);
        return;
    }

    if (!pageSnaps.length) {
        return;
    }

    const pageIndex = getCurrentPageIndexFromSnap(api, pageSnaps);
    const nextSnap = pageSnaps[pageIndex + 1];
    if (nextSnap !== undefined) {
        api.scrollTo(nextSnap);
    }
}

export function scrollToPrevPage(api: CarouselApi, pageSnaps: number[]): void {
    if (pageSnaps.length > 0) {
        const pageIndex = getCurrentPageIndexFromSnap(api, pageSnaps);
        const prevSnap = pageSnaps[pageIndex - 1];
        if (prevSnap !== undefined) {
            api.scrollTo(prevSnap);
            return;
        }
    }

    const prevIndex = getPrevPageScrollIndex(api);
    if (prevIndex !== null) {
        api.scrollTo(prevIndex);
    }
}
